import { supabase } from "@/lib/supabase-client";
import { ShiftType } from "@/types/db/shifts";
import { ShiftUserStatusType } from "@/types/db/shift_users";
import { FileMetadata } from "@/types/files";

// Shifts with these statuses are considered "closed" and don't count as having issues
const EXCLUDED_SHIFT_STATUSES = ["CANCELED", "CANCELED - BILLABLE", "COMPLETE"];

// User statuses that count as "fulfilled" for staffing requirements
const FULFILLED_USER_STATUSES = ["ACCEPTED", "COMPLETE", "ATTENDANCE EXCEPTION"];

/**
 * Check if a shift has a staffing issue.
 * A shift has an issue if:
 * 1. Status is not CANCELED, CANCELED - BILLABLE, or COMPLETE
 * 2. Any position has fewer users with ACCEPTED/COMPLETE/ATTENDANCE EXCEPTION status than needed (quantity)
 */
export function hasStaffingIssue(shift: ShiftType): boolean {
  // Skip excluded statuses
  if (shift.status && EXCLUDED_SHIFT_STATUSES.includes(shift.status)) {
    return false;
  }

  // Check each position for understaffing
  return (
    shift.shift_positions?.some((position) => {
      const needed = position.quantity || 0;
      if (needed === 0) return false; // No staffing requirement

      const fulfilled =
        position.shift_users?.filter((user) => FULFILLED_USER_STATUSES.includes(user.status || "")).length || 0;

      return fulfilled < needed;
    }) ?? false
  );
}

export const shiftsService = {
  // Get count of shifts with staffing issues
  async getShiftsWithIssuesCount(): Promise<number> {
    const { data, error } = await supabase
      .from("shifts")
      .select(
        `
        id,
        status,
        shift_positions(
          quantity,
          shift_users(status)
        )
      `,
      )
      .not("status", "in", `(${EXCLUDED_SHIFT_STATUSES.map((s) => `"${s}"`).join(",")})`)
      .eq("archived", false);

    if (error) {
      console.error("Error fetching shifts with issues:", error.message);
      return 0;
    }

    // Count shifts that have staffing issues
    return data?.filter((shift) => hasStaffingIssue(shift as ShiftType)).length || 0;
  },

  // Get shift stats (counts and issues) for past/future relative to a reference date
  async getShiftStats(referenceDate?: Date): Promise<{
    pastShifts: number;
    pastIssues: number;
    futureShifts: number;
    futureIssues: number;
  }> {
    const splitDate = referenceDate ? new Date(referenceDate) : new Date();
    splitDate.setHours(0, 0, 0, 0);
    const splitDateISO = splitDate.toISOString();

    // Fetch past shifts (before reference date)
    const { data: pastData, error: pastError } = await supabase
      .from("shifts")
      .select(
        `
        id,
        status,
        shift_positions(
          quantity,
          shift_users(status)
        )
      `,
      )
      .lt("start_time", splitDateISO)
      .eq("archived", false);

    // Fetch future shifts (reference date and onwards)
    const { data: futureData, error: futureError } = await supabase
      .from("shifts")
      .select(
        `
        id,
        status,
        shift_positions(
          quantity,
          shift_users(status)
        )
      `,
      )
      .gte("start_time", splitDateISO)
      .eq("archived", false);

    if (pastError) {
      console.error("Error fetching past shifts:", pastError.message);
    }
    if (futureError) {
      console.error("Error fetching future shifts:", futureError.message);
    }

    const pastShifts = pastData?.length || 0;
    const pastIssues = pastData?.filter((shift) => hasStaffingIssue(shift as ShiftType)).length || 0;
    const futureShifts = futureData?.length || 0;
    const futureIssues = futureData?.filter((shift) => hasStaffingIssue(shift as ShiftType)).length || 0;

    return { pastShifts, pastIssues, futureShifts, futureIssues };
  },

  async getShifts(options?: {
    startDate?: string;
    endDate?: string;
    limit?: number;
    includeArchived?: boolean;
  }): Promise<ShiftType[]> {
    const { data, error } = await supabase.rpc("get_shifts", {
      p_include_archived: options?.includeArchived ?? false,
      p_start_date: options?.startDate ?? null,
      p_end_date: options?.endDate ?? null,
      p_limit: options?.limit ?? null,
    });
    if (error) throw Error(error.message);
    return ((data as any[]) || []) as ShiftType[];
  },

  async getShiftHeatmap(options: {
    startDate: string;
    endDate: string;
    excludeShiftId?: string;
  }): Promise<{ id: string; name: string; start_time: string; end_time: string }[]> {
    let query = supabase
      .from("shifts")
      .select("id, name, start_time, end_time")
      .gte("start_time", options.startDate)
      .lt("start_time", options.endDate)
      .not("status", "in", '("CANCELED","CANCELED - BILLABLE")');
    if (options.excludeShiftId) {
      query = query.neq("id", options.excludeShiftId);
    }
    const { data, error } = await query.order("start_time", { ascending: true });
    if (error) {
      console.error("Error fetching shift heatmap:", error.message);
      return [];
    }
    return (data ?? []) as { id: string; name: string; start_time: string; end_time: string }[];
  },

  async getShiftsWithUsers(): Promise<ShiftType[]> {
    const { data, error } = await supabase
      .from("shifts")
      .select(
        `
        *,
        project:projects(id, name, location_id),
        shift_positions(
          *,
          shift_users(
            *,
            user:users(id, first_name, last_name, email_address, preferred_name)
          )
        )
      `,
      )
      .order("created_at", { ascending: false });
    if (error) {
      throw Error(error.message);
    }
    return data as ShiftType[];
  },

  async getShiftsAfter(date: string): Promise<ShiftType[]> {
    const { data, error } = await supabase
      .from("shifts")
      .select(
        `
        *,
        project:projects(id, name, location_id),
        shift_positions(
          *,
          shift_users(
            *,
            user:users(id, first_name, last_name, email_address, preferred_name)
          )
        )
      `,
      )
      .gte("end_date", date)
      .order("created_at", { ascending: false });
    if (error) {
      throw Error(error.message);
    }
    return data as ShiftType[];
  },

  async acceptShift(shiftId: string, userId: string, accept: boolean): Promise<boolean> {
    const status = accept ? "ACCEPTED" : "DECLINED";

    const { error } = await supabase.from("shift_users").update({ status }).eq("shift_id", shiftId).eq("id", userId);

    if (error) {
      throw new Error(`Failed to update shift status: ${error.message}`);
    }

    return true;
  },

  async getInvitedShifts(id: string, futureOnly = true): Promise<ShiftType[]> {
    let query = supabase
      .from("shifts")
      .select(
        `
        *,
        project:projects(id, name, location_id),
        shift_positions!inner(
          *,
          shift_users!inner(
            *,
            user:users(id, first_name, last_name, email_address, preferred_name)
          )
        )
      `,
      )
      .eq("shift_positions.shift_users.id", id);

    if (futureOnly) {
      query = query.gt("end_time", new Date().toISOString());
    }

    const { data, error } = await query;
    if (error) {
      throw error;
    }
    return data as ShiftType[];
  },

  async getShiftsByUserId(id: string, futureOnly = true): Promise<ShiftType[]> {
    // Validate user ID to prevent unnecessary API calls
    if (!id || id.trim() === "") {
      throw Error("Invalid user ID provided, returning empty array");
    }

    // Single query following FK chain: shift_users → shift_positions → shifts → projects
    const { data, error } = await supabase
      .from("shift_users")
      .select(
        `
        shift_position:shift_positions(
          *,
          shift:shifts(
            *,
            project:projects(id, name, location_id, meta_data, client:clients(id, name)),
            shift_positions(
              *,
              shift_users(
                *,
                user:users(id, first_name, last_name, email_address, preferred_name)
              )
            )
          )
        )
      `,
      )
      .eq("id", id);

    if (error) {
      throw Error(`Error fetching shifts for user ${id}: ${error.message}`);
    }

    if (!data || data.length === 0) {
      return [];
    }

    // Extract unique shifts from the nested structure
    // Each shift_users record has ONE shift_position with ONE shift
    const shiftsMap = new Map<string, ShiftType>();
    data.forEach((record: any) => {
      // shift_position and shift come back as single objects, not arrays
      const shiftPosition = record.shift_position;
      if (shiftPosition?.shift) {
        const shift = shiftPosition.shift;
        if (shift.id) {
          shiftsMap.set(shift.id, shift as ShiftType);
        }
      }
    });

    let shifts = Array.from(shiftsMap.values());

    // Apply futureOnly filter - exclude closed/canceled shifts
    if (futureOnly) {
      shifts = shifts.filter((shift) => !shift.status || !EXCLUDED_SHIFT_STATUSES.includes(shift.status));
    }

    // Sort by start_time
    shifts.sort((a, b) => {
      const aTime = a.start_time ? new Date(a.start_time).getTime() : 0;
      const bTime = b.start_time ? new Date(b.start_time).getTime() : 0;
      return aTime - bTime;
    });

    // Collect all unique position_ids from all shifts
    const positionIds = new Set<string>();
    shifts.forEach((shift) => {
      shift.shift_positions?.forEach((sp: any) => {
        if (sp.position_id) positionIds.add(sp.position_id);
      });
    });

    // Batch fetch positions from lists.positions
    let positionsMap = new Map();
    if (positionIds.size > 0) {
      const { data: positions } = await supabase
        .schema("lists")
        .from("positions")
        .select("id, name, description, billing_rate, meta_data")
        .in("id", Array.from(positionIds));

      positions?.forEach((pos) => positionsMap.set(pos.id, pos));
    }

    // Enrich shifts with position data
    shifts = shifts.map((shift) => ({
      ...shift,
      shift_positions: shift.shift_positions?.map((sp: any) => ({
        ...sp,
        position: sp.position_id ? positionsMap.get(sp.position_id) : undefined,
      })),
    }));

    return shifts;
  },

  async getShiftsBySupervisorId(userId: string, futureOnly = true): Promise<ShiftType[]> {
    // Validate user ID to prevent unnecessary API calls
    if (!userId || userId.trim() === "") {
      throw Error("Invalid user ID provided");
    }

    // Query shift_supervisors to get shifts where user is a supervisor
    const { data, error } = await supabase
      .from("shift_supervisors")
      .select(
        `
        shift:shifts(
          *,
          project:projects(id, name, location_id, meta_data, client:clients(id, name)),
          shift_positions(
            *,
            shift_users(
              *,
              user:users(id, first_name, last_name, email_address, preferred_name)
            )
          )
        )
      `,
      )
      .eq("user_id", userId);

    if (error) {
      throw Error(`Error fetching supervised shifts for user ${userId}: ${error.message}`);
    }

    if (!data || data.length === 0) {
      return [];
    }

    // Extract unique shifts from the nested structure
    const shiftsMap = new Map<string, ShiftType>();
    data.forEach((record: any) => {
      if (record.shift?.id) {
        shiftsMap.set(record.shift.id, record.shift as ShiftType);
      }
    });

    let shifts = Array.from(shiftsMap.values());

    // Apply futureOnly filter - exclude closed/canceled shifts
    if (futureOnly) {
      shifts = shifts.filter((shift) => !shift.status || !EXCLUDED_SHIFT_STATUSES.includes(shift.status));
    }

    // Sort by start_time
    shifts.sort((a, b) => {
      const aTime = a.start_time ? new Date(a.start_time).getTime() : 0;
      const bTime = b.start_time ? new Date(b.start_time).getTime() : 0;
      return aTime - bTime;
    });

    // Collect all unique position_ids from all shifts
    const positionIds = new Set<string>();
    shifts.forEach((shift) => {
      shift.shift_positions?.forEach((sp: any) => {
        if (sp.position_id) positionIds.add(sp.position_id);
      });
    });

    // Batch fetch positions from lists.positions
    let positionsMap = new Map();
    if (positionIds.size > 0) {
      const { data: positions } = await supabase
        .schema("lists")
        .from("positions")
        .select("id, name, description, billing_rate, meta_data")
        .in("id", Array.from(positionIds));

      positions?.forEach((pos) => positionsMap.set(pos.id, pos));
    }

    // Enrich shifts with position data
    shifts = shifts.map((shift) => ({
      ...shift,
      shift_positions: shift.shift_positions?.map((sp: any) => ({
        ...sp,
        position: sp.position_id ? positionsMap.get(sp.position_id) : undefined,
      })),
    }));

    return shifts;
  },

  async getShiftById(id: string): Promise<ShiftType | null> {
    const { data, error } = await supabase.rpc("get_shift", { p_shift_id: id });
    if (error) throw new Error(`Error fetching shift ${id}: ${error.message}`);
    if (!data) return null;

    const shift = data as any;

    // File attachments still need client-side hydration (storage signed URLs)
    let filesMap = new Map();
    try {
      const [shiftFilesData, projectFilesData] = await Promise.all([
        this.getFiles(id),
        shift.project_id
          ? (await import("@/services/projectService")).projectService.getFiles(shift.project_id)
          : Promise.resolve([]),
      ]);
      const allFiles = [...shiftFilesData, ...projectFilesData];
      filesMap = new Map(allFiles.map((file: any) => [file.id, file]));
    } catch (fileError) {
      console.warn("Failed to fetch files for shift:", fileError);
    }

    // Hydrate file attachments onto shift_positions
    if (shift.shift_positions) {
      shift.shift_positions = shift.shift_positions.map((sp: any) => {
        const shiftFileIds = sp.meta_data?.files?.shifts || [];
        const projectFileIds = sp.meta_data?.files?.projects || [];
        const allFileIds = [...shiftFileIds, ...projectFileIds];
        const attachments = allFileIds.map((fileId: string) => filesMap.get(fileId)).filter(Boolean);
        return { ...sp, attachments };
      });
    }

    return shift as ShiftType;
  },

  async getShiftsByProjectId(project_id: string): Promise<ShiftType[]> {
    const { data, error } = await supabase
      .from("shifts")
      .select(
        `
        *,
        project:projects(id, name, location_id),
        shift_positions(
          *,
          shift_users(
            *,
            user:users(id, first_name, last_name, email_address, preferred_name)
          )
        )
      `,
      )
      .eq("project_id", project_id);
    if (error) {
      throw Error(`Error fetching shift by project ${project_id}\r\n${error.message}`);
    }

    // Collect all unique position_ids from shift_positions
    const positionIds = new Set<string>();
    data?.forEach((shift: any) => {
      shift.shift_positions?.forEach((sp: any) => {
        if (sp.position_id) positionIds.add(sp.position_id);
      });
    });

    // Batch fetch positions from lists.positions
    let positionsMap = new Map<string, { id: string; name: string }>();
    if (positionIds.size > 0) {
      const { data: positions } = await supabase
        .schema("lists")
        .from("positions")
        .select("id, name")
        .in("id", Array.from(positionIds));

      if (positions) {
        positionsMap = new Map(positions.map((p) => [p.id, p]));
      }
    }

    // Enrich shift_positions with position data
    return data?.map((shift: any) => ({
      ...shift,
      shift_positions: shift.shift_positions?.map((sp: any) => ({
        ...sp,
        position: sp.position_id ? positionsMap.get(sp.position_id) : undefined,
      })),
    })) as ShiftType[];
  },

  async getShiftsByClientId(client_id: string): Promise<ShiftType[]> {
    const { data, error } = await supabase
      .from("shifts")
      .select(
        `
        *,
        project:projects(id, name, location_id),
        shift_positions(
          *,
          shift_users(
            *,
            user:users(id, first_name, last_name, email_address, preferred_name)
          )
        )
      `,
      )
      .eq("client_id", client_id)
      .order("created_at", { ascending: false });
    if (error) {
      throw Error(`Error fetching shifts by client ${client_id}\r\n${error.message}`);
    }

    // Collect all unique position_ids from shift_positions
    const positionIds = new Set<string>();
    data?.forEach((shift: any) => {
      shift.shift_positions?.forEach((sp: any) => {
        if (sp.position_id) positionIds.add(sp.position_id);
      });
    });

    // Batch fetch positions from lists.positions
    let positionsMap = new Map<string, { id: string; name: string }>();
    if (positionIds.size > 0) {
      const { data: positions } = await supabase
        .schema("lists")
        .from("positions")
        .select("id, name")
        .in("id", Array.from(positionIds));

      if (positions) {
        positionsMap = new Map(positions.map((p) => [p.id, p]));
      }
    }

    // Enrich shift_positions with position data
    return data?.map((shift: any) => ({
      ...shift,
      shift_positions: shift.shift_positions?.map((sp: any) => ({
        ...sp,
        position: sp.position_id ? positionsMap.get(sp.position_id) : undefined,
      })),
    })) as ShiftType[];
  },

  async addShift(start_time: string, end_time: string, invited_users?: string): Promise<ShiftType | null> {
    const { data, error } = await supabase
      .from("tasks")
      .insert([{ start_time, end_time, invited_users }])
      .select()
      .single();
    if (error) {
      console.error("Error adding task:", error.message);
      return null;
    }
    return data as ShiftType;
  },

  async inviteUsers(shift_id: string, user_ids: string[]): Promise<boolean> {
    const { error } = await supabase.rpc("add_invited_users", { shift_id, user_ids });
    if (error) {
      console.error("Error updating task status:", error.message);
      return false;
    }
    return true;
  },

  // async updateScheduleStatus(id: string, status: Shift["status"]): Promise<boolean> {
  //   const { error } = await supabase.from("tasks").update({ status }).eq("id", id);
  //
  //   if (error) {
  //     console.error("Error updating task status:", error.message);
  //     return false;
  //   }
  //   return true;
  // },

  async updateShift(id: string, updates: Partial<ShiftType>): Promise<ShiftType | null> {
    const { data, error } = await supabase
      .from("shifts")
      .update(updates)
      .eq("id", id)
      .select("*, project: projects (id, name)")
      .single();

    if (error) {
      console.error("Error updating shift:", error.message);
      return null;
    }
    return data as ShiftType;
  },

  async createShift(shiftData: {
    project_id: string;
    name?: string;
    start_time?: string | null;
    end_time?: string | null;
    status?: string;
    description?: string | null;
    meta_data?: any;
  }): Promise<ShiftType> {
    // Get project to get client_id and tenant_id
    const { data: project, error: projectError } = await supabase
      .from("projects")
      .select("client_id, tenant_id, name")
      .eq("id", shiftData.project_id)
      .single();

    if (projectError || !project) {
      throw new Error(`Failed to fetch project: ${projectError?.message || "Project not found"}`);
    }

    const { data: shift, error: shiftError } = await supabase
      .from("shifts")
      .insert([
        {
          project_id: shiftData.project_id,
          client_id: project.client_id,
          tenant_id: project.tenant_id,
          name: shiftData.name || `Shift for ${project.name}`,
          description: shiftData.description ?? null,
          start_time: shiftData.start_time || null,
          end_time: shiftData.end_time || null,
          status: shiftData.status || "PENDING",
          meta_data: shiftData.meta_data || {},
        },
      ])
      .select("*, project: projects (id, name)")
      .single();

    if (shiftError) {
      throw new Error(`Failed to create shift: ${shiftError.message}`);
    }

    return shift as ShiftType;
  },

  async deleteShift(id: string): Promise<boolean> {
    // Get shift data for link table cleanup
    const { data: shift } = await supabase
      .from("shifts")
      .select("tenant_id, client_id, project_id")
      .eq("id", id)
      .single();

    // 1. Get all shift_positions for this shift
    const { data: positions, error: fetchError } = await supabase
      .from("shift_positions")
      .select("id")
      .eq("shift_id", id);

    if (fetchError) {
      console.error("Error fetching shift positions:", fetchError.message);
      return false;
    }

    // 2. Delete each position using deleteShiftPosition (which properly cascades to shift_users)
    for (const position of positions || []) {
      const success = await this.deleteShiftPosition(position.id);
      if (!success) {
        console.error(`Failed to delete position ${position.id}`);
        return false;
      }
    }

    // 3. Delete shift_abilities from link table
    if (shift?.tenant_id && shift?.client_id && shift?.project_id) {
      const { error: abilitiesError } = await supabase
        .schema("link")
        .from("shift_abilities")
        .delete()
        .eq("id", id)
        .eq("tenant_id", shift.tenant_id)
        .eq("client_id", shift.client_id)
        .eq("project_id", shift.project_id);

      if (abilitiesError) {
        console.error("Error deleting shift abilities:", abilitiesError.message);
      }
    }

    // 4. Delete shift_restrictions from link table
    if (shift?.tenant_id && shift?.client_id && shift?.project_id) {
      const { error: restrictionsError } = await supabase
        .schema("link")
        .from("shift_restrictions")
        .delete()
        .eq("id", id)
        .eq("tenant_id", shift.tenant_id)
        .eq("client_id", shift.client_id)
        .eq("project_id", shift.project_id);

      if (restrictionsError) {
        console.error("Error deleting shift restrictions:", restrictionsError.message);
      }
    }

    // 5. Finally delete the shift
    const { error } = await supabase.from("shifts").delete().eq("id", id);
    if (error) {
      console.error("Error deleting shift:", error.message);
      return false;
    }
    return true;
  },

  // Shift Positions CRUD methods
  async createShiftPosition(
    shiftId: string,
    positionId: string,
    locationId?: string,
    quantity?: number,
    preferredQuantity?: number,
    supervisor?: boolean,
    meta_data?: any,
  ): Promise<any> {
    const { data: shift, error: shiftError } = await supabase
      .from("shifts")
      .select("tenant_id, client_id, project_id")
      .eq("id", shiftId)
      .single();

    if (shiftError || !shift) {
      throw new Error(`Failed to get shift: ${shiftError?.message || "Shift not found"}`);
    }

    const { data, error } = await supabase
      .from("shift_positions")
      .insert({
        tenant_id: shift.tenant_id,
        client_id: shift.client_id,
        project_id: shift.project_id,
        shift_id: shiftId,
        position_id: positionId,
        location_id: locationId,
        quantity: quantity || 1,
        preferred_quantity: preferredQuantity,
        supervisor: supervisor || false,
        meta_data: meta_data || {},
      })
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to create shift position: ${error.message}`);
    }
    return data;
  },

  async updateShiftPosition(shiftPositionId: string, updates: any): Promise<any> {
    const { data, error } = await supabase
      .from("shift_positions")
      .update(updates)
      .eq("id", shiftPositionId)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to update shift position: ${error.message}`);
    }
    return data;
  },

  async deleteShiftPosition(shiftPositionId: string): Promise<boolean> {
    // Delete the shift_position - database CASCADE handles shift_users automatically
    const { error } = await supabase.from("shift_positions").delete().eq("id", shiftPositionId);
    if (error) {
      console.error("Error deleting shift position:", error.message);
      return false;
    }
    return true;
  },

  // Shift Users CRUD methods
  async addShiftUser(shiftPositionId: string, userId: string, status: ShiftUserStatusType = "QUEUED"): Promise<any> {
    const { data: position, error: posError } = await supabase
      .from("shift_positions")
      .select("tenant_id, client_id, project_id, shift_id")
      .eq("id", shiftPositionId)
      .single();

    if (posError || !position) {
      throw new Error(`Failed to get shift position: ${posError?.message || "Position not found"}`);
    }

    const { data, error } = await supabase
      .from("shift_users")
      .insert({
        id: userId,
        tenant_id: position.tenant_id,
        client_id: position.client_id,
        project_id: position.project_id,
        shift_id: position.shift_id,
        shift_position_id: shiftPositionId,
        status: status,
      })
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to add shift user: ${error.message}`);
    }
    return data;
  },

  /**
   * Add multiple users to a shift position in a single batch operation.
   * More efficient than looping through addShiftUser for multiple users.
   */
  async addShiftUsersBatch(
    shiftPositionId: string,
    userIds: string[],
    status: ShiftUserStatusType = "QUEUED",
  ): Promise<any[]> {
    if (userIds.length === 0) return [];

    // Get position details for required fields
    const { data: position, error: posError } = await supabase
      .from("shift_positions")
      .select("tenant_id, client_id, project_id, shift_id")
      .eq("id", shiftPositionId)
      .single();

    if (posError || !position) {
      throw new Error(`Failed to get shift position: ${posError?.message || "Position not found"}`);
    }

    // Build batch insert records
    const records = userIds.map((userId) => ({
      id: userId,
      tenant_id: position.tenant_id,
      client_id: position.client_id,
      project_id: position.project_id,
      shift_id: position.shift_id,
      shift_position_id: shiftPositionId,
      status: status,
    }));

    // Use upsert with ignoreDuplicates to handle race conditions where
    // a user might already be assigned (e.g., from another concurrent operation)
    const { data, error } = await supabase
      .from("shift_users")
      .upsert(records, {
        onConflict: "id,tenant_id,client_id,project_id,shift_id,shift_position_id",
        ignoreDuplicates: true,
      })
      .select();

    if (error) {
      throw new Error(`Failed to batch add shift users: ${error.message}`);
    }
    return data || [];
  },

  async updateShiftUserStatus(shiftPositionId: string, userId: string, status: ShiftUserStatusType): Promise<any> {
    const { data, error } = await supabase
      .from("shift_users")
      .update({ status })
      .eq("shift_position_id", shiftPositionId)
      .eq("id", userId)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to update shift user status: ${error.message}`);
    }
    return data;
  },

  /**
   * Update status for all users matching a filter on a position
   * Useful for batch operations like resetting ACCEPTED to INVITED when position changes
   */
  async updateShiftUserStatusBatch(
    shiftPositionId: string,
    fromStatus: ShiftUserStatusType,
    toStatus: ShiftUserStatusType,
  ): Promise<any[]> {
    const { data, error } = await supabase
      .from("shift_users")
      .update({ status: toStatus })
      .eq("shift_position_id", shiftPositionId)
      .eq("status", fromStatus)
      .select();

    if (error) {
      throw new Error(`Failed to batch update shift user status: ${error.message}`);
    }
    return data || [];
  },

  async removeShiftUser(shiftPositionId: string, userId: string): Promise<boolean> {
    const { error } = await supabase
      .from("shift_users")
      .delete()
      .eq("shift_position_id", shiftPositionId)
      .eq("id", userId);

    if (error) {
      console.error("Error removing shift user:", error.message);
      return false;
    }
    return true;
  },

  async updateShiftUserMeta(shiftId: string, userId: string, metaUpdate: Record<string, unknown>): Promise<boolean> {
    // First get current meta_data
    const { data: current } = await supabase
      .from("shift_users")
      .select("meta_data")
      .eq("shift_id", shiftId)
      .eq("id", userId)
      .single();

    const mergedMeta = { ...(current?.meta_data || {}), ...metaUpdate };

    const { error } = await supabase
      .from("shift_users")
      .update({ meta_data: mergedMeta })
      .eq("shift_id", shiftId)
      .eq("id", userId);

    if (error) {
      console.error("Failed to update shift_user meta_data:", error);
      return false;
    }
    return true;
  },

  // Shift Supervisors CRUD methods
  async addShiftSupervisor(shiftId: string, userId: string): Promise<any> {
    const { data: shift, error: shiftError } = await supabase
      .from("shifts")
      .select("tenant_id, client_id, project_id")
      .eq("id", shiftId)
      .single();

    if (shiftError || !shift) {
      throw new Error(`Failed to get shift: ${shiftError?.message || "Shift not found"}`);
    }

    const { data, error } = await supabase
      .from("shift_supervisors")
      .insert({
        tenant_id: shift.tenant_id,
        client_id: shift.client_id,
        project_id: shift.project_id,
        shift_id: shiftId,
        user_id: userId,
        permissions: {},
        meta_data: {},
      })
      .select(
        `
        *,
        user:users(id, first_name, last_name, email_address, preferred_name, meta_data)
      `,
      )
      .single();

    if (error) {
      throw new Error(`Failed to add shift supervisor: ${error.message}`);
    }
    return data;
  },

  async removeShiftSupervisor(shiftId: string, userId: string): Promise<boolean> {
    const { error } = await supabase.from("shift_supervisors").delete().eq("shift_id", shiftId).eq("user_id", userId);

    if (error) {
      console.error("Error removing shift supervisor:", error.message);
      return false;
    }
    return true;
  },

  // File management methods
  async uploadFile(shiftId: string, file: File, uploadedBy: string): Promise<FileMetadata> {
    // Get tenant_id from shift record
    const { data: tenantData, error: tenantError } = await supabase
      .from("shifts")
      .select("tenant_id")
      .eq("id", shiftId)
      .single();

    if (tenantError || !tenantData) {
      throw new Error(`Failed to get shift tenant: ${tenantError?.message || "Shift not found"}`);
    }

    const fileId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const filePath = `${tenantData.tenant_id}/${shiftId}/${fileId}-${file.name}`;

    // Upload file to storage
    const { error: uploadError } = await supabase.storage.from("shifts").upload(filePath, file);

    if (uploadError) {
      throw new Error(`Upload failed: ${uploadError.message}`);
    }

    // Create file metadata (store path, not URL)
    const fileMetadata: FileMetadata = {
      id: fileId,
      name: file.name,
      size: file.size,
      type: file.type,
      url: filePath, // Store path for signed URL generation
      bucket: "shifts", // Store bucket name
      uploadedAt: new Date().toISOString(),
      uploadedBy: uploadedBy,
    };

    // No database metadata tracking needed - files are listed directly from storage
    return fileMetadata;
  },

  // Helper to infer mimetype from file extension
  _getMimeTypeFromExtension(fileName: string): string {
    const ext = fileName.split(".").pop()?.toLowerCase();
    const mimeTypes: Record<string, string> = {
      // Images
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
      png: "image/png",
      gif: "image/gif",
      webp: "image/webp",
      svg: "image/svg+xml",
      bmp: "image/bmp",
      ico: "image/x-icon",
      // Documents
      pdf: "application/pdf",
      doc: "application/msword",
      docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      xls: "application/vnd.ms-excel",
      xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ppt: "application/vnd.ms-powerpoint",
      pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      txt: "text/plain",
      csv: "text/csv",
      // Video
      mp4: "video/mp4",
      mov: "video/quicktime",
      avi: "video/x-msvideo",
      webm: "video/webm",
      // Audio
      mp3: "audio/mpeg",
      wav: "audio/wav",
      ogg: "audio/ogg",
      // Archives
      zip: "application/zip",
      rar: "application/x-rar-compressed",
    };
    return mimeTypes[ext || ""] || "application/octet-stream";
  },

  async getFiles(shiftId: string): Promise<FileMetadata[]> {
    const { data: tenantData, error: tenantError } = await supabase
      .from("shifts")
      .select("tenant_id")
      .eq("id", shiftId)
      .single();

    if (tenantError || !tenantData) {
      throw new Error(`Failed to get shift tenant: ${tenantError?.message || "Shift not found"}`);
    }

    const storagePath = `${tenantData.tenant_id}/${shiftId}`;
    const { data: files, error: listError } = await supabase.storage.from("shifts").list(storagePath);

    if (listError) {
      throw new Error(`Failed to list shift files: ${listError.message}`);
    }

    if (!files || files.length === 0) {
      return [];
    }

    const fileMetadata: FileMetadata[] = files.map((file) => {
      const dashIndex = file.name.indexOf("-");
      const fileId = dashIndex > 0 ? file.name.substring(0, dashIndex) : file.name;
      const fileName = dashIndex > 0 ? file.name.substring(dashIndex + 1) : file.name;

      return {
        id: fileId,
        name: fileName,
        size: file.metadata?.size || 0,
        type: file.metadata?.mimetype || this._getMimeTypeFromExtension(fileName),
        url: `${storagePath}/${file.name}`, // Store path for signed URL generation
        bucket: "shifts", // Store bucket name
        uploadedAt: file.created_at || new Date().toISOString(),
        uploadedBy: "unknown",
      };
    });

    return fileMetadata;
  },

  async deleteFile(shiftId: string, fileId: string): Promise<boolean> {
    const { data: tenantData, error: tenantError } = await supabase
      .from("shifts")
      .select("tenant_id")
      .eq("id", shiftId)
      .single();

    if (tenantError || !tenantData) {
      throw new Error(`Failed to get shift tenant: ${tenantError?.message || "Shift not found"}`);
    }

    const storagePath = `${tenantData.tenant_id}/${shiftId}`;
    const { data: files, error: listError } = await supabase.storage.from("shifts").list(storagePath);

    if (listError) {
      throw new Error(`Failed to list shift files: ${listError.message}`);
    }

    const fileToDelete = files?.find((file) => file.name.startsWith(`${fileId}-`));

    if (!fileToDelete) {
      throw new Error("File not found in storage");
    }

    const filePath = `${storagePath}/${fileToDelete.name}`;
    const { error: storageError } = await supabase.storage.from("shifts").remove([filePath]);

    if (storageError) {
      throw new Error(`Failed to delete from storage: ${storageError.message}`);
    }

    return true;
  },

  async renameFile(shiftId: string, fileId: string, newName: string): Promise<FileMetadata> {
    const { data: tenantData, error: tenantError } = await supabase
      .from("shifts")
      .select("tenant_id")
      .eq("id", shiftId)
      .single();

    if (tenantError || !tenantData) {
      throw new Error(`Failed to get shift tenant: ${tenantError?.message || "Shift not found"}`);
    }

    const storagePath = `${tenantData.tenant_id}/${shiftId}`;
    const { data: files, error: listError } = await supabase.storage.from("shifts").list(storagePath);

    if (listError) {
      throw new Error(`Failed to list shift files: ${listError.message}`);
    }

    const fileToRename = files?.find((file) => file.name.startsWith(`${fileId}-`));

    if (!fileToRename) {
      throw new Error("File not found in storage");
    }

    const oldPath = `${storagePath}/${fileToRename.name}`;
    const newPath = `${storagePath}/${fileId}-${newName}`;

    const { error: moveError } = await supabase.storage.from("shifts").move(oldPath, newPath);

    if (moveError) {
      throw new Error(`Failed to rename file: ${moveError.message}`);
    }

    return {
      id: fileId,
      name: newName,
      size: fileToRename.metadata?.size || 0,
      type: fileToRename.metadata?.mimetype || "application/octet-stream",
      url: newPath,
      bucket: "shifts",
      uploadedAt: fileToRename.created_at || new Date().toISOString(),
      uploadedBy: "unknown",
    };
  },

  // Link table management methods
  async syncShiftAbilities(
    shiftId: string,
    tenantId: string,
    clientId: string,
    projectId: string,
    abilities: { ability: { id: string } }[],
  ): Promise<void> {
    // Delete all existing shift abilities
    const { error: deleteError } = await supabase
      .schema("link")
      .from("shift_abilities")
      .delete()
      .eq("id", shiftId)
      .eq("tenant_id", tenantId)
      .eq("client_id", clientId)
      .eq("project_id", projectId);

    if (deleteError) {
      throw new Error(`Failed to delete existing shift abilities: ${deleteError.message}`);
    }

    // Insert new abilities
    if (abilities.length > 0) {
      const records = abilities.map((a) => ({
        id: shiftId,
        tenant_id: tenantId,
        client_id: clientId,
        project_id: projectId,
        ability_id: a.ability.id,
      }));

      const { error: insertError } = await supabase.schema("link").from("shift_abilities").insert(records);

      if (insertError) {
        throw new Error(`Failed to insert shift abilities: ${insertError.message}`);
      }
    }
  },

  async syncShiftRestrictions(
    shiftId: string,
    tenantId: string,
    clientId: string,
    projectId: string,
    restrictions: { restriction: { id: string } }[],
  ): Promise<void> {
    // Delete all existing shift restrictions
    const { error: deleteError } = await supabase
      .schema("link")
      .from("shift_restrictions")
      .delete()
      .eq("id", shiftId)
      .eq("tenant_id", tenantId)
      .eq("client_id", clientId)
      .eq("project_id", projectId);

    if (deleteError) {
      throw new Error(`Failed to delete existing shift restrictions: ${deleteError.message}`);
    }

    // Insert new restrictions
    if (restrictions.length > 0) {
      const records = restrictions.map((r) => ({
        id: shiftId,
        tenant_id: tenantId,
        client_id: clientId,
        project_id: projectId,
        restriction_id: r.restriction.id,
      }));

      const { error: insertError } = await supabase.schema("link").from("shift_restrictions").insert(records);

      if (insertError) {
        throw new Error(`Failed to insert shift restrictions: ${insertError.message}`);
      }
    }
  },
};
